import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { endOfDay, parseISO } from 'date-fns'
import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ArrowUp, Delete } from '@factorialco/f0-react/icons/app'
import {
  BankingValueObjectsPaymentRequestSourceTypeEnum,
  BankingValueObjectsPaymentRequestStatusEnum,
  PaymentRequestFieldsFragment,
  PaymentRequestsDataCollectionDocument,
  PaymentRequestsPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import {
  PayPaymentRequestsInBulkModal,
  type SelectAllFilters,
} from 'modules/banking/components/PayPaymentRequestsInBulkModal'
import { useDeletePaymentRequest } from 'modules/banking/hooks/useDeletePaymentRequest'
import { getSourceTypeLabel } from 'modules/banking/lib/paymentHelpers'
import { AllPaymentRequestsInsightCards } from 'modules/banking/components/TreasuryInsightCard'
import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { useLegalEntityFilter } from 'modules/employees/lib/filters/legalEntity'
import { useBeneficiaryFilter } from 'modules/banking/pages/PaymentRequestsPage/useBeneficiaryFilter'
import { useTreasuryDateRange } from 'modules/finance/hooks/useTreasuryDateRange'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const TRANSFER_FEATURE_FLAG_NAME = 'dev_finance_swan_reactivation'

const VALID_SOURCE_TYPES: BankingValueObjectsPaymentRequestSourceTypeEnum[] = [
  'invoice',
  'payroll',
  'expensable',
  'salary_advance_request',
  'compensation',
]
const validSourceTypeValues = new Set<string>(VALID_SOURCE_TYPES)

const filterValidSourceTypes = (
  sourceTypes: string[] | undefined
): BankingValueObjectsPaymentRequestSourceTypeEnum[] | undefined => {
  if (!sourceTypes?.length) return undefined
  const filtered = sourceTypes.filter((s): s is BankingValueObjectsPaymentRequestSourceTypeEnum =>
    validSourceTypeValues.has(s)
  )
  return filtered.length > 0 ? filtered : undefined
}

const VALID_STATUSES: BankingValueObjectsPaymentRequestStatusEnum[] = [
  'init',
  'in_payment',
  'in_progress',
  'paid',
]

const NON_PAID_STATUSES: BankingValueObjectsPaymentRequestStatusEnum[] = [
  'init',
  'in_payment',
  'in_progress',
]
const validStatusValues = new Set<string>(VALID_STATUSES)

const filterValidStatuses = (
  statuses: string[] | undefined
): BankingValueObjectsPaymentRequestStatusEnum[] | undefined => {
  if (!statuses?.length) return undefined
  const filtered = statuses.filter((s): s is BankingValueObjectsPaymentRequestStatusEnum =>
    validStatusValues.has(s)
  )
  return filtered.length > 0 ? filtered : undefined
}

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning'

const getStatusColor = (status: BankingValueObjectsPaymentRequestStatusEnum): StatusColor => {
  const statusColorMap: Record<BankingValueObjectsPaymentRequestStatusEnum, StatusColor> = {
    init: 'warning',
    in_payment: 'info',
    in_progress: 'info',
    paid: 'positive',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getStatusLabel = (status: BankingValueObjectsPaymentRequestStatusEnum): string => {
  const statusLabelMap: Record<BankingValueObjectsPaymentRequestStatusEnum, string> = {
    init: i18n.t('banking.paymentRequests.status.init'),
    in_payment: i18n.t('banking.paymentRequests.status.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.status.inProgress'),
    paid: i18n.t('banking.paymentRequests.status.paid'),
  }
  return statusLabelMap[status] ?? status
}

// Helper functions to safely extract filter values
const getStringArrayFilter = (value: unknown): string[] | undefined => {
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return value.length > 0 ? value : undefined
  }
  return undefined
}

const PaymentRequestsPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedPaymentRequests, setSelectedPaymentRequests] = useState<
    PaymentRequestFieldsFragment[]
  >([])
  const [sendToPayModalOpen, toggleSendToPayModal] = useToggle(false)
  const [paymentRequestToDeleteId, setPaymentRequestToDeleteId] = useState<string | null>(null)
  const [selectAllState, setSelectAllState] = useState<{
    selectAll: boolean
    excludedIds: string[]
    filters: SelectAllFilters
    selectedCount: number
  } | null>(null)
  const { submitDelete, loading: deleteLoading } = useDeletePaymentRequest()

  // Read status filter from URL params (e.g., when navigating from Overview widget)
  const { status: statusParam } = useSearchParams<{ status?: string }>()
  const initialStatusFilter = useMemo(() => {
    if (statusParam) {
      return { status: [statusParam] }
    }
    return undefined
  }, [statusParam])

  // Track dueDate filter from DataCollection to pass to InsightCards
  const [dueDateFilter, setDueDateFilter] = useState<{ between: [string, string] } | undefined>()
  // Track current filters from DataCollection for select-all bulk actions
  const [currentCollectionFilters, setCurrentCollectionFilters] = useState<Record<string, unknown>>(
    {}
  )
  const [currentCollectionSearch, setCurrentCollectionSearch] = useState<string | undefined>()

  const { data, loading, error } = useQuery(PaymentRequestsPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [TRANSFER_FEATURE_FLAG_NAME],
    },
  })

  const canSeeSepaTransferAction =
    data?.features?.featuresConnection?.nodes.find(
      (feature: { name: string; enabled: boolean }) => feature.name === TRANSFER_FEATURE_FLAG_NAME
    )?.enabled ?? false

  const requesterFilter = useEmployeesFilter({
    label: i18n.t('banking.paymentRequests.filters.requester'),
  })

  const legalEntityFilter = {
    ...useLegalEntityFilter(),
    label: i18n.t('banking.paymentRequests.filters.legalEntity'),
  }

  const beneficiaryFilter = useBeneficiaryFilter(
    i18n.t('banking.paymentRequests.filters.beneficiary')
  )

  const goToPaymentDetail = (paymentId?: string) => {
    if (paymentId) {
      navigate(
        navigation.finance.treasury.banking.payments.paymentId.fullPath({
          id: paymentId,
        })
      )
    } else {
      navigate(navigation.finance.banking.payments.fullPath())
    }
  }

  // Default filter: show "init" (pending) preset
  // Use initialStatusFilter from URL params if present, otherwise default to "init"
  const defaultFilters = useMemo(
    () => initialStatusFilter ?? { status: ['init'] },
    [initialStatusFilter]
  )

  // Shared date range from localStorage (synced with Overview and other Treasury pages)
  const {
    startDate: sharedStartDate,
    endDate: sharedEndDate,
    onDateRangeChange: setSharedDateRange,
  } = useTreasuryDateRange()

  // Navigation filter for visible date picker (DateNavigator)
  // Use 'range' mode by default for bijection with Overview widget links
  // Initialize from shared localStorage date range
  const navigationFilters = useMemo(
    () => ({
      dueDate: {
        type: 'date-navigator' as const,
        defaultGranularity: 'range' as const,
        granularity: ['day', 'week', 'month', 'year', 'range'],
        defaultValue: {
          from: sharedStartDate,
          to: sharedEndDate,
        },
      },
    }),
    [sharedStartDate, sharedEndDate]
  )

  // Convert DataCollection dueDateRange filter format { from, to } to GraphQL format { between: [from, to] }
  // Note: values can be Date objects or strings (when restored from localStorage)
  // The end date uses endOfDay because due_date is a datetime column — a bare date
  // would be interpreted as midnight, missing all records during that day.
  const convertDueDateFilter = useCallback(
    (dueDateRange: { from?: Date | string; to?: Date | string } | undefined) => {
      if (!dueDateRange?.from || !dueDateRange?.to) return undefined
      const toDate = (value: Date | string): Date =>
        value instanceof Date ? value : parseISO(value)
      const start = toDate(dueDateRange.from).toISOString()
      const end = endOfDay(toDate(dueDateRange.to)).toISOString()
      const between: [string, string] = [start, end]
      return { between }
    },
    []
  )

  const handleStateChange = useCallback(
    (state: {
      navigationFilters?: { dueDate?: { value?: { from?: Date | string; to?: Date | string } } }
      filters?: Record<string, unknown>
      search?: string
    }) => {
      const dueDateRange = state.navigationFilters?.dueDate?.value
      if (dueDateRange?.from && dueDateRange?.to) {
        // Update shared localStorage for sync with Overview and other Treasury pages
        const fromDate =
          dueDateRange.from instanceof Date ? dueDateRange.from : moment(dueDateRange.from).toDate()
        const toDate =
          dueDateRange.to instanceof Date ? dueDateRange.to : moment(dueDateRange.to).toDate()
        setSharedDateRange(fromDate, toDate)
      }
      setDueDateFilter(convertDueDateFilter(dueDateRange))
      if (state.filters) {
        setCurrentCollectionFilters(state.filters)
      }
      setCurrentCollectionSearch(state.search)
    },
    [convertDueDateFilter, setSharedDateRange]
  )

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: PaymentRequestsDataCollectionDocument,
      mapData: (data) =>
        data.banking.paymentRequestsConnection.edges.map(
          (edge: { node: PaymentRequestFieldsFragment }) => edge.node
        ),
      mapVariables: ({ search, filters, sortings: mapSortings, navigationFilters: navFilters }) => {
        const dueDateStatusFilter = getStringArrayFilter(filters.dueDateStatus)
        const hasDueDateStatusFilter = dueDateStatusFilter && dueDateStatusFilter.length > 0
        const statuses = filterValidStatuses(getStringArrayFilter(filters.status))
        // When filtering by due date status, exclude paid PRs (same logic as PaymentsOverdueWidget)
        const effectiveStatuses = hasDueDateStatusFilter && !statuses ? NON_PAID_STATUSES : statuses

        // Build dueDate filter from the navigation filter.
        // The end date uses endOfDay because due_date is a datetime column — a bare
        // date would be interpreted as midnight, missing all records during that day.
        const dueDateRange = navFilters?.dueDate?.value
        const toDate = (value: Date | string): Date =>
          value instanceof Date ? value : parseISO(value)
        const dueDateBetween =
          dueDateRange?.from && dueDateRange?.to
            ? {
                between: [
                  toDate(dueDateRange.from).toISOString(),
                  endOfDay(toDate(dueDateRange.to)).toISOString(),
                ],
              }
            : undefined

        const requesterIds = getStringArrayFilter(filters.requester)
        const legalEntityIds = getStringArrayFilter(filters.legalEntity)
        const sourceTypes = filterValidSourceTypes(getStringArrayFilter(filters.sourceTypes))
        const beneficiaryNames = getStringArrayFilter(filters.beneficiary)

        return {
          requesterIds,
          legalEntityIds,
          statuses: effectiveStatuses,
          sourceTypes,
          beneficiaryNames,
          onTime: dueDateStatusFilter?.includes('on_time') || undefined,
          dueThisWeek: dueDateStatusFilter?.includes('due_this_week') || undefined,
          overdue: dueDateStatusFilter?.includes('overdue') || undefined,
          filter: dueDateBetween ? { dueDate: dueDateBetween } : undefined,
          sortOrder: mapSortings?.[0],
          search,
        }
      },
      sortings: {
        label: {
          label: i18n.t('banking.paymentRequests.sortings.name'),
        },
        amountCents: {
          label: i18n.t('banking.paymentRequests.sortings.amount'),
        },
        dueDate: {
          label: i18n.t('banking.paymentRequests.sortings.dueDate'),
        },
        createdAt: {
          label: i18n.t('banking.paymentRequests.sortings.createdAt'),
        },
        status: {
          label: i18n.t('banking.paymentRequests.sortings.status'),
        },
      },
      defaultSortings: {
        field: 'dueDate' as const,
        order: 'asc' as const,
      },
      paginationType: 'pages',
      perPage: 10,
      mapPagination: (data) => ({
        total: data.banking.paymentRequestsConnection.totalCount ?? 0,
        hasNextPage: data.banking.paymentRequestsConnection.pageInfo.hasNextPage ?? false,
      }),
      search: { enabled: true },
      presets: [
        {
          label: i18n.t('banking.paymentRequests.status.init'),
          filter: { status: ['init'] },
        },
      ],
      currentFilters: defaultFilters,
      navigationFilters,
      filters: {
        requester: requesterFilter,
        legalEntity: legalEntityFilter,
        beneficiary: beneficiaryFilter,
        status: {
          label: i18n.t('banking.paymentRequests.filters.status'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('banking.paymentRequests.status.init'), value: 'init' },
              { label: i18n.t('banking.paymentRequests.status.inPayment'), value: 'in_payment' },
              { label: i18n.t('banking.paymentRequests.status.inProgress'), value: 'in_progress' },
              { label: i18n.t('banking.paymentRequests.status.paid'), value: 'paid' },
            ],
          },
        },
        dueDateStatus: {
          label: i18n.t('banking.paymentRequests.filters.dueDateStatus'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.onTime'),
                value: 'on_time',
              },
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.dueThisWeek'),
                value: 'due_this_week',
              },
              {
                label: i18n.t('banking.paymentRequests.filters.dueDateOptions.overdue'),
                value: 'overdue',
              },
            ],
          },
        },
        sourceTypes: {
          label: i18n.t('banking.paymentRequests.filters.source'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.invoice'),
                value: 'invoice',
              },
              {
                label: i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.payroll'),
                value: 'payroll',
              },
              {
                label: i18n.t('banking.paymentRequests.itemDetailSidePanel.sourceTypes.expensable'),
                value: 'expensable',
              },
              {
                label: i18n.t(
                  'banking.paymentRequests.itemDetailSidePanel.sourceTypes.salary_advance_request'
                ),
                value: 'salary_advance_request',
              },
              {
                label: i18n.t(
                  'banking.paymentRequests.itemDetailSidePanel.sourceTypes.compensation'
                ),
                value: 'compensation',
              },
            ],
          },
        },
      },
      itemUrl: (item: PaymentRequestFieldsFragment) =>
        navigation.finance.treasury.banking.paymentRequests.paymentRequestId.fullPath({
          paymentRequestId: item.id,
        }),
      selectable: (item: PaymentRequestFieldsFragment) =>
        item.status === 'init' ? String(item.id) : undefined,
      allPagesSelection: true,
      itemActions: (item: PaymentRequestFieldsFragment) => {
        const actions = []

        if (item.status === 'init') {
          actions.push({
            label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
            icon: ArrowUp,
            type: 'primary' as const,
            onClick: () => {
              setSelectedPaymentRequests([item])
              toggleSendToPayModal(true)()
            },
          })
        }

        const nonDeleteableStatuses = ['paid', 'in_payment', 'in_progress']
        if (!nonDeleteableStatuses.includes(item.status)) {
          actions.push({
            id: 'delete',
            label: i18n.t('banking.paymentRequests.actions.delete'),
            icon: Delete,
            critical: true,
            onClick: () => {
              setPaymentRequestToDeleteId(item.id)
            },
          })
        }

        return actions.length > 0 ? actions : undefined
      },
      bulkActions: () => ({
        primary: [
          {
            id: 'send-to-pay',
            label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
          },
        ],
        secondary: [],
      }),
    },
    []
  )

  const handleBulkAction = (
    action: string,
    items: {
      itemsStatus: readonly { checked: boolean; item: PaymentRequestFieldsFragment }[]
      allSelected?: boolean | 'indeterminate'
      selectedIds?: readonly (string | number)[]
      selectedCount?: number
    }
  ) => {
    if (action !== 'send-to-pay') return

    const isSelectAllActive = items.allSelected === true || items.allSelected === 'indeterminate'

    if (isSelectAllActive) {
      const excludedIds =
        items.allSelected === 'indeterminate'
          ? items.itemsStatus?.filter((item) => !item.checked).map((item) => item.item.id) || []
          : []

      const requesterIds = getStringArrayFilter(currentCollectionFilters?.requester)
      const sourceTypes = filterValidSourceTypes(
        getStringArrayFilter(currentCollectionFilters?.sourceTypes)
      )
      const dueDateStatusFilter = getStringArrayFilter(currentCollectionFilters?.dueDateStatus)

      const selectAllPayload = {
        selectAll: true,
        excludedIds,
        filters: {
          requesterIds,
          sourceTypes,
          search: currentCollectionSearch || undefined,
          onTime: dueDateStatusFilter?.includes('on_time') || undefined,
          dueThisWeek: dueDateStatusFilter?.includes('due_this_week') || undefined,
          overdue: dueDateStatusFilter?.includes('overdue') || undefined,
          dueDateFrom: dueDateFilter?.between[0],
          dueDateTo: dueDateFilter?.between[1],
        },
        selectedCount:
          items.selectedCount ?? items.itemsStatus.filter((item) => item.checked).length,
      }
      setSelectAllState(selectAllPayload)
      toggleSendToPayModal(true)()
    } else {
      const selectedItems = items.itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      setSelectAllState(null)
      setSelectedPaymentRequests(selectedItems)
      toggleSendToPayModal(true)()
    }
  }

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.paymentRequests.columns.paymentRequest'),
        sorting: 'label' as const,
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: row.label,
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.source'),
        width: 220,
        render: (row: PaymentRequestFieldsFragment) => {
          const uniqueSourceTypes = Array.from(
            new Set(row.lineItems.map((item) => item.sourceType))
          )
          if (uniqueSourceTypes.length === 0) return { type: 'text' as const, value: '-' }
          return {
            type: 'tagList' as const,
            value: {
              type: 'raw' as const,
              max: uniqueSourceTypes.length,
              tags: uniqueSourceTypes.map((sourceType) => ({
                text: getSourceTypeLabel(sourceType),
              })),
            },
          }
        },
      },
      {
        label: i18n.t('banking.paymentRequests.columns.legalEntity'),
        width: 220,
        render: (row: PaymentRequestFieldsFragment) => {
          const uniqueLegalEntityNames = Array.from(
            new Set(
              row.lineItems
                .map((item) => item.legalEntity?.legalName)
                .filter((name): name is string => Boolean(name))
            )
          )
          if (uniqueLegalEntityNames.length === 0) return { type: 'text' as const, value: '-' }
          return { type: 'text' as const, value: uniqueLegalEntityNames.join(', ') }
        },
      },
      {
        label: i18n.t('banking.paymentRequests.columns.requester'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'person' as const,
          value: {
            firstName: row.requester?.access?.fullName ?? '-',
            lastName: '',
          },
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.beneficiary'),
        width: 220,
        hidden: true,
        render: (row: PaymentRequestFieldsFragment) => {
          const uniqueBeneficiaries = Array.from(
            new Set(
              row.lineItems
                .map((item) => item.beneficiaryName)
                .filter((name): name is string => name != null)
            )
          )
          if (uniqueBeneficiaries.length === 0) return { type: 'text' as const, value: '-' }
          return { type: 'text' as const, value: uniqueBeneficiaries.join(', ') }
        },
      },
      {
        label: i18n.t('banking.paymentRequests.columns.items'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: String(row.lineItemsConnection?.totalCount ?? 0),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.amount'),
        sorting: 'amountCents' as const,
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({ amount: row.amountCents, currency: row.currency, locale }),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.dueDate'),
        sorting: 'dueDate' as const,
        render: (row: PaymentRequestFieldsFragment) =>
          convertDate(row.dueDate, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.status'),
        sorting: 'status' as const,
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(row.status),
            status: getStatusColor(row.status),
          },
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
          allowColumnHiding: true,
        },
      },
    ],
    [columns]
  )

  return (
    <ResponsivePage.Body error={error} loading={loading} authorized variant="full">
      <Box flexDirection="column" gap="s16" width="full" height="full">
        <AllPaymentRequestsInsightCards
          currency={company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'}
          locale={locale}
          dueDateFilter={dueDateFilter}
        />
        <OneDataCollection
          id="payment-requests/v1"
          storage={{
            features: initialStatusFilter
              ? ['sortings', 'search']
              : ['filters', 'sortings', 'search'],
          }}
          fullHeight
          source={dataSource}
          visualizations={visualizations}
          onBulkAction={handleBulkAction}
          onStateChange={handleStateChange}
        />
      </Box>
      {sendToPayModalOpen && (
        <PayPaymentRequestsInBulkModal
          onSuccess={goToPaymentDetail}
          onClose={() => {
            toggleSendToPayModal(false)()
            setSelectAllState(null)
          }}
          paymentRequests={selectAllState ? undefined : selectedPaymentRequests}
          enablePaymentMethodFilter={canSeeSepaTransferAction}
          selectAll={selectAllState?.selectAll}
          excludedPaymentRequestIds={selectAllState?.excludedIds}
          filters={selectAllState?.filters}
          selectedCount={selectAllState?.selectedCount}
        />
      )}

      {paymentRequestToDeleteId !== null && (
        <Modal
          illustration="delete"
          title={i18n.t('banking.paymentRequests.delete_modal.title')}
          description={i18n.t('banking.paymentRequests.delete_modal.description')}
          onClose={() => setPaymentRequestToDeleteId(null)}
          onSubmit={async () => {
            await submitDelete({ id: paymentRequestToDeleteId })
            setPaymentRequestToDeleteId(null)
          }}
          footer={
            <>
              <Button
                onClick={() => setPaymentRequestToDeleteId(null)}
                type="secondary"
                disabled={deleteLoading}
              >
                {i18n.t('banking.paymentRequests.delete_modal.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  await submitDelete({ id: paymentRequestToDeleteId })
                  setPaymentRequestToDeleteId(null)
                }}
                disabled={deleteLoading}
                {...(deleteLoading && { iconName: 'circle' })}
              >
                {i18n.t('banking.paymentRequests.delete_modal.confirm')}
              </Button>
            </>
          }
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PaymentRequestsPage
